import type { ReactNode } from 'react';

import OptionalContainer from 'components/OptionalContainer';
import type { InputHTMLAttributes } from 'types';
import { cn, cnm } from 'utils/classNames';
import { empty } from 'utils/helpers';

import { getCheckboxRadioClassName, type RadioCheckboxColor } from './helpers';

// Omit onClick to enforce the use of onChange.
export interface Props extends Omit<
	InputHTMLAttributes<HTMLInputElement>,
	'onClick' | 'type'
> {
	color?: RadioCheckboxColor;
	/**
	 * Accessibly mark and display the field as disabled.
	 *
	 * NOTE! This results in an ARIA disable rather than a full, native disable
	 * which means a form submission will still include the value.
	 */
	disabled?: boolean;
	hiddenLabel?: boolean;
	id: string;
	inputClassName?: string;
	invalid?: boolean;
	label: ReactNode;
	labelClassName?: string;
	valid?: boolean;
}

export default function Checkbox({
	className,
	color = 'green',
	disabled,
	hiddenLabel = false,
	id,
	inputClassName,
	invalid = false,
	label,
	labelClassName,
	onChange,
	required,
	// Currently not used but must be destructed to not be passed to the DOM
	// as an unknown attribute.
	// oxlint-disable-next-line typescript/no-unused-vars
	valid = false,
	...attrs
}: Props) {
	return (
		<div className={cnm('flex items-start', className)}>
			<input
				{...attrs}
				type="checkbox"
				className={getCheckboxRadioClassName({
					color,
					disabled,
					extra: cn(
						'rounded-xs hover:not-focus-visible:ring-1 hover:not-focus-visible:ring-current',
						inputClassName,
					),
				})}
				aria-invalid={invalid}
				onChange={disabled ? empty.func : onChange}
				aria-disabled={disabled ? 'true' : undefined}
				id={id}
				required={required}
			/>
			<label
				className={cnm(
					!hiddenLabel && 'pl-2',
					!disabled && 'cursor-pointer',
					// Disable pointer events to stop the checkbox from showing mouse
					// feedback, since it's only ARIA disabled.
					disabled && 'pointer-events-none cursor-default opacity-50',
					labelClassName,
				)}
				htmlFor={id}
			>
				{/* Hide the label via child element to keep the label element
				available for expanding the checkbox hit area. */}
				<OptionalContainer
					as="span"
					hasContainer={hiddenLabel}
					className="sr-only"
				>
					{label}
				</OptionalContainer>
			</label>
		</div>
	);
}
Checkbox.displayName = 'FormUI_Checkbox';
